import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';
import { QuizLayout, StepNavigation } from '../../components/quiz';
import { AuthModal } from '../../components/shared';
import TrustStep from './steps/TrustStep';
import GoalsStep from './steps/GoalsStep';
import IndustryStep from './steps/IndustryStep';
import FinancialsStep from './steps/FinancialsStep';
import DebtStep from './steps/DebtStep';
import { quizToApiRequest } from '../../utils/quizToApi';
import { track, EVENTS } from '../../utils/analytics';
import type { SelectableCardOption, BusinessQuizAnswers } from '../../types';

const BUSINESS_GOALS: SelectableCardOption[] = [
  { id: 'cash_flow', icon: '💰', title: 'I want to master my cash flow', description: 'Know where every dollar goes' },
  { id: 'growth', icon: '📈', title: 'I want to grow my business', description: 'Build the financial structure to scale' },
  { id: 'pay_debt', icon: '💳', title: 'I want to eliminate business debt', description: 'Become debt-free faster' },
  { id: 'tax_planning', icon: '📋', title: 'I want to keep more of what I earn', description: 'Manage your profits strategically' },
  { id: 'save_emergency', icon: '🛡️', title: 'I want a safety net for my business', description: 'Build an emergency reserve' },
  { id: 'other', icon: '✏️', title: 'Other', description: 'You tell us, we\'ll build it' },
];

const TOTAL_STEPS = 5;

export default function BusinessQuiz() {
  const navigate = useNavigate();
  const { state, updateQuizAnswers, setApiRequest } = useApp();
  const [currentStep, setCurrentStep] = useState(1);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const answers = state.quizAnswers as Partial<BusinessQuizAnswers>;

  function trackCurrentStep() {
    switch (currentStep) {
      case 1:
        track(EVENTS.TRUST_PAGE_VIEWED, { userType: 'BUSINESS' });
        break;
      case 2:
        track(EVENTS.QUIZ_GOAL_DONE, { goals: answers.goals, otherGoalText: answers.otherGoalText });
        break;
      case 3:
        track(EVENTS.QUIZ_INDUSTRY_DONE, { industry: answers.industry });
        break;
      case 4:
        track(EVENTS.QUIZ_FINANCIALS_DONE, { monthlyRevenue: answers.monthlyRevenue, monthlyExpenses: answers.monthlyExpenses });
        break;
      case 5:
        track(EVENTS.QUIZ_DEBT_DONE, { hasDebt: answers.hasDebt, debtTypes: answers.debtTypes });
        break;
    }
  }

  function goNext() {
    trackCurrentStep();

    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
    } else {
      track(EVENTS.QUIZ_COMPLETED, { userType: 'BUSINESS', ...answers });

      // Build API request and show auth modal
      const apiRequest = quizToApiRequest({
        ...answers,
        userType: 'BUSINESS',
      });
      if (apiRequest) {
        setApiRequest(apiRequest);
        setShowAuthModal(true);
      }
    }
  }

  function goBack() {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    } else {
      navigate('/quiz');
    }
  }

  function canContinue(): boolean {
    switch (currentStep) {
      case 1:
        return true; // Trust step - always can continue
      case 2:
        return (answers.goals?.length ?? 0) > 0;
      case 3:
        return Boolean(answers.industry);
      case 4:
        return Boolean(answers.monthlyRevenue) && Boolean(answers.monthlyExpenses);
      case 5:
        return answers.hasDebt !== null && answers.hasDebt !== undefined;
      default:
        return true;
    }
  }

  function renderStep() {
    switch (currentStep) {
      case 1:
        return <TrustStep userType="BUSINESS" />;
      case 2:
        return (
          <GoalsStep
            goals={answers.goals || []}
            onChange={(goals) => updateQuizAnswers({ goals })}
            options={BUSINESS_GOALS}
            otherGoalText={answers.otherGoalText || ''}
            onOtherGoalTextChange={(otherGoalText) => updateQuizAnswers({ otherGoalText })}
          />
        );
      case 3:
        return (
          <IndustryStep
            industry={answers.industry || ''}
            onChange={(industry) => updateQuizAnswers({ industry })}
          />
        );
      case 4:
        return (
          <FinancialsStep
            monthlyRevenue={answers.monthlyRevenue || ''}
            monthlyExpenses={answers.monthlyExpenses || ''}
            onRevenueChange={(monthlyRevenue) => updateQuizAnswers({ monthlyRevenue })}
            onExpensesChange={(monthlyExpenses) => updateQuizAnswers({ monthlyExpenses })}
          />
        );
      case 5:
        return (
          <DebtStep
            hasDebt={answers.hasDebt ?? null}
            debtTypes={answers.debtTypes || []}
            isBusinessDebt={true}
            onHasDebtChange={(hasDebt) => updateQuizAnswers({ hasDebt })}
            onDebtTypesChange={(debtTypes) => updateQuizAnswers({ debtTypes })}
          />
        );
      default:
        return null;
    }
  }

  return (
    <>
      <QuizLayout
        currentStep={currentStep}
        totalSteps={TOTAL_STEPS}
        navigation={
          <StepNavigation
            onBack={goBack}
            onContinue={goNext}
            canContinue={canContinue()}
            continueLabel={currentStep === TOTAL_STEPS ? 'Build My Plan Now' : 'Continue'}
          />
        }
      >
        {renderStep()}
      </QuizLayout>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
      />
    </>
  );
}
